import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { OnboardingLayout } from '../OnboardingLayout';
import { Label } from '../../../components/ui/Label';
import { Input } from '../../../components/ui/Input';
import { FieldError } from '../../../components/ui/FieldError';
import { Alert } from '../../../components/ui/Alert';
import { isValidCnpj, maskCnpj, normalizeCnpj } from '../../../utils/cnpj';
import { lookupCnpj } from '../brasilapi';
import { createOrg, selectOrg, ApiProblemError } from '../../../lib/api-client';
import { useAuth } from '../AuthContext';
import { useApiKeys } from '../ApiKeysContext';
import { useOnboardingState } from '../useOnboardingState';

/**
 * Tela 2 — CNPJ (/onboarding/cnpj). On submit: creates the org, selects it
 * (sets the `orgId` custom claim server-side), forces a fresh ID token so
 * the claim is visible client-side, then — per the onboarding architecture
 * decided up front (see ApiKeysContext.tsx) — mints the `trv_test_`/
 * `trv_live_` key pair BEFORE navigating to Tela 3, since that screen's
 * `PUT /v1/providers/{provider}/credentials` call needs the `trv_live_` key
 * already in memory to work correctly (a `trv_test_` key there would
 * silently skip real Secret Manager persistence — see
 * providers.service.ts#putCredentials's `isSandbox` branch).
 */
export function CnpjStep() {
  const cnpjId = useId();
  const legalNameId = useId();
  const tradeNameId = useId();

  const { user, refreshOrgClaim } = useAuth();
  const { ensureKeys } = useApiKeys();
  const { update } = useOnboardingState();
  const navigate = useNavigate();

  const [cnpjInput, setCnpjInput] = useState('');
  const [legalName, setLegalName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);

  const [cnpjError, setCnpjError] = useState<string | undefined>(undefined);
  const [legalNameError, setLegalNameError] = useState<string | undefined>(undefined);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const lastLookedUpRef = useRef<string | null>(null);

  const digits = normalizeCnpj(cnpjInput);

  useEffect(() => {
    if (digits.length !== 14 || !isValidCnpj(digits) || lastLookedUpRef.current === digits) {
      return;
    }
    lastLookedUpRef.current = digits;
    setLookupLoading(true);
    setLookupFailed(false);
    lookupCnpj(digits)
      .then((result) => {
        if (result) {
          setLegalName((prev) => prev || result.legal_name);
          setTradeName((prev) => prev || result.trade_name || '');
        } else {
          setLookupFailed(true);
        }
      })
      .finally(() => setLookupLoading(false));
  }, [digits]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    setCnpjError(undefined);
    setLegalNameError(undefined);

    if (digits.length !== 14 || !isValidCnpj(digits)) {
      setCnpjError('CNPJ inválido — confira os dígitos.');
      return;
    }
    if (legalName.trim().length === 0) {
      setLegalNameError('Informe a razão social.');
      return;
    }
    if (!user) {
      return;
    }

    setSubmitting(true);
    try {
      const idToken = await user.getIdToken();
      const trimmedTradeName = tradeName.trim();
      const org = await createOrg(idToken, {
        cnpj: digits,
        legal_name: legalName.trim(),
        ...(trimmedTradeName ? { trade_name: trimmedTradeName } : {}),
      });

      await selectOrg(idToken, org.id);
      await refreshOrgClaim();

      // Mint the onboarding API key pair now, in memory, before Tela 3
      // needs the live one — see this file's top docstring.
      const freshIdToken = await user.getIdToken();
      await ensureKeys(freshIdToken);

      update({ step: 'providers', orgId: org.id });
      navigate('/onboarding/providers');
    } catch (err) {
      if (err instanceof ApiProblemError) {
        const fieldMessage = err.fieldError('cnpj');
        if (fieldMessage || err.status === 409) {
          setCnpjError(fieldMessage ?? 'Já existe uma organização cadastrada para este CNPJ.');
        } else {
          setFormError(err.problem.detail ?? err.problem.title);
        }
      } else {
        setFormError('Não foi possível criar a organização. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <OnboardingLayout
      step="cnpj"
      title="Dados da empresa"
      description="Informe o CNPJ da sua empresa — usamos para identificar sua organização no Trivio."
    >
      {formError ? (
        <Alert variant="error" title="Não foi possível continuar">
          {formError}
        </Alert>
      ) : null}

      <form className="flex flex-col gap-5" onSubmit={handleSubmit} noValidate>
        <div className="flex flex-col gap-2">
          <Label htmlFor={cnpjId}>CNPJ</Label>
          <Input
            id={cnpjId}
            inputMode="numeric"
            autoComplete="off"
            placeholder="00.000.000/0000-00"
            value={cnpjInput}
            invalid={Boolean(cnpjError)}
            aria-describedby={cnpjError ? `${cnpjId}-error` : undefined}
            onChange={(event) => setCnpjInput(maskCnpj(event.target.value))}
            required
          />
          <FieldError id={`${cnpjId}-error`} message={cnpjError} />
          {lookupLoading ? <p className="text-sm text-muted-foreground">Consultando CNPJ…</p> : null}
          {lookupFailed ? (
            <p className="text-sm text-muted-foreground">
              Não encontramos esse CNPJ na Receita — preencha os dados manualmente.
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor={legalNameId}>Razão social</Label>
          <Input
            id={legalNameId}
            value={legalName}
            invalid={Boolean(legalNameError)}
            aria-describedby={legalNameError ? `${legalNameId}-error` : undefined}
            onChange={(event) => setLegalName(event.target.value)}
            required
          />
          <FieldError id={`${legalNameId}-error`} message={legalNameError} />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor={tradeNameId}>Nome fantasia (opcional)</Label>
          <Input id={tradeNameId} value={tradeName} onChange={(event) => setTradeName(event.target.value)} />
        </div>

        <Button type="submit" variant="default" disabled={submitting} className="mt-2 self-start">
          {submitting ? 'Criando organização…' : 'Continuar'}
        </Button>
      </form>
    </OnboardingLayout>
  );
}
